/**
 * NUDIENT: Bästsäljarbandet byter efter vald modell, paket G2, M-104 punkt 1
 * och specen 4.6. Plan: nudient/PLAN-G2-G4-G5.md. Laddas av
 * sections/nudient-related.liquid när inställningen follow_model är på och
 * sidan saknar produkt.
 *
 * - Sidan visar först bandets kollektion, de vanliga bästsäljarna.
 * - Utan vald modell visar bandet standardmodellen, den översta i
 *   ordningslistan, getDefaultModel, M-007 och specen kriterium 17,
 *   beslutat av Vidar 4 november 2026. Rubriken skriver ut modellens namn.
 *   Standardmodellen sparas aldrig och läggs aldrig i bandets länk, som då
 *   leder dit den gjorde förut. Har skaltypen inga skal för standardmodellen
 *   står bandets egen lista kvar, utan rad om att modellen saknas.
 * - Med vald modell hämtas modellens kollektion, modellpostens url, sorterad
 *   efter bäst säljande, med den alternativa mallen
 *   templates/collection.nudient-bestseller-cards.json, utan sidhuvud och
 *   sidfot, som skriver korten med sections/nudient-related-cards.liquid.
 *   Mallen sparar kortens block, bild, märken, namn, färg och pris, som D7 i
 *   produktmallarna. Korten byts. Rubriken blir model_heading med modellens namn.
 * - Bandet hoppar inte: listans höjd hålls kvar under bytet, och korten byts
 *   på samma plats. Listan rullas tillbaka till början, och knapparna räknas
 *   om av D2:s skript. Antalet kort är bandets, data-max.
 * - Följer model:change. Ett svar som kommer för en modell kunden redan har
 *   bytt bort från används inte.
 * - Saknar modellen kollektion, eller har den inga kort, står bästsäljarna
 *   kvar.
 * - Skalband, M-147: med data-model-collection, inställningen
 *   model_collection, hämtas skaltypens kollektion med vald modell i
 *   filtret i stället för modellens kollektion, sorterad efter bäst
 *   säljande, med samma mall. Bandets länk leder till skaltypens kollektion
 *   med modellen, M-127. Har skaltypen inga skal för modellen döljs bandet,
 *   och det visas igen med en annan modell eller utan vald modell. Rubriken
 *   och etiketten byts inte. Utan vald modell visas bandets egen lista och
 *   länk som förut.
 * - Sidor som bara handlar om en skaltyp, M-147 tillägget,
 *   data-keep-missing: bandet döljs inte när skaltypen saknar skal för
 *   modellen. Det visar sina egna kort och länk, standardmodellens, med
 *   raden data-missing-text ovanför, där [type], [model] och [default] byts
 *   mot skaltypen, vald modell och standardmodellen.
 * - Sidan hoppar inte när ett band ovanför synfältet döljs eller visas igen:
 *   den första synliga sektionen i synfältet mäts före och efter, och sidan rullas
 *   skillnaden. Mätt i Chrome 30 oktober 2026: utan det flyttades sidan
 *   660 pixlar när Bold-bandet visades igen ovanför Thin-bandet. Har
 *   webbläsaren redan hållit kvar platsen är skillnaden 0.
 *
 * Värden som inte är tokens:
 * - best-selling: Shopifys sortering efter bäst säljande.
 * - filter.p.m.attribute.compatible_models: filtrets parameter, M-013.
 * - 1: pixlar som räknas som oförändrat.
 * - nudient-bestseller-cards: den alternativa mallens namn i ?view=.
 * - [model]: platsen för modellens namn i rubriken, M-094 punkt 7.
 */

import { findModel, getDefaultModel, getModel } from '@nudient/model-store';

const VIEW = 'nudient-bestseller-cards';
const PARAM = 'filter.p.m.attribute.compatible_models';

/**
 * Gör ändringen utan att sidan hoppar, M-147.
 * @param {HTMLElement} band
 * @param {() => void} change
 */
const keepPlace = (band, change) => {
  const ref = [...document.querySelectorAll('main .shopify-section')].find((section) => {
    const rect = section.getBoundingClientRect();
    return rect.height > 0 && rect.bottom > 0;
  });
  if (!ref || ref.contains(band)) {
    change();
    return;
  }
  const before = ref.getBoundingClientRect().top;
  change();
  const diff = ref.getBoundingClientRect().top - before;
  // instant, eftersom en mjuk rullning ger fel mått för nästa band som byts
  if (Math.abs(diff) > 1) window.scrollBy({ top: diff, behavior: 'instant' });
};

class NudientRelatedModel {
  /** @param {HTMLElement} band */
  constructor(band) {
    this.band = band;
    this.track = band.querySelector('[data-track]');
    this.heading = band.querySelector('.nudient-related__heading');
    this.original = {
      cards: this.track?.innerHTML || '',
      heading: this.heading?.textContent || '',
      label: band.getAttribute('aria-label') || '',
    };
    this.max = Number(band.dataset.max) || 6;
    this.caseCollection = band.dataset.modelCollection || '';
    this.link = band.querySelector('.nudient-related__foot a');
    this.originalHref = this.link?.getAttribute('href') || '';
    this.note = band.querySelector('[data-model-note]');
    this.shown = '';
    this.request = 0;
    this.apply();
    document.addEventListener('model:change', () => this.apply());
  }

  async apply() {
    const chosen = findModel(getModel());
    const model = chosen || getDefaultModel();
    this.isDefault = !chosen;
    const key = model ? `${chosen ? '' : 'default:'}${model.handle}` : '';
    if (key === this.shown) return;
    const request = ++this.request;

    if (!model || (!model.url && !this.caseCollection)) {
      this.#swap(this.original.cards, this.original.heading, this.original.label);
      this.#show(this.originalHref);
      if (this.note) this.note.hidden = true;
      this.shown = '';
      return;
    }

    if (this.caseCollection) {
      await this.#applyCaseType(model, key, request);
      return;
    }

    try {
      const url = new URL(model.url, window.location.origin);
      url.searchParams.set('sort_by', 'best-selling');
      url.searchParams.set('view', VIEW);
      const response = await fetch(url.toString());
      if (!response.ok) return;
      const html = await response.text();
      if (request !== this.request) return;
      const list = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-nudient-related-cards]');
      const cards = [...(list?.children || [])].slice(0, this.max);
      if (cards.length === 0) return;
      const heading = (this.band.dataset.modelHeading || this.original.heading).replace('[model]', model.name);
      this.#swap(cards.map((card) => card.outerHTML).join(''), heading, heading);
      this.shown = key;
    } catch {}
  }

  /**
   * Skalband, M-147: skaltypens kollektion med vald modell i filtret.
   * @param {{ handle: string, model?: string, name?: string }} model
   * @param {string} key
   * @param {number} request
   */
  async #applyCaseType(model, key, request) {
    const value = model.model || model.name || '';
    const url = new URL(this.caseCollection, window.location.origin);
    url.searchParams.set(PARAM, value);
    const href = url.pathname + url.search;
    url.searchParams.set('sort_by', 'best-selling');
    url.searchParams.set('view', VIEW);
    try {
      const response = await fetch(url.toString());
      if (!response.ok) return;
      const html = await response.text();
      if (request !== this.request) return;
      const list = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-nudient-related-cards]');
      const cards = [...(list?.children || [])].slice(0, this.max);
      if (cards.length === 0 && this.isDefault) {
        keepPlace(this.band, () => {
          this.#swap(this.original.cards, this.original.heading, this.original.label);
          this.#show(this.originalHref);
          if (this.note) this.note.hidden = true;
        });
      } else if (cards.length === 0 && 'keepMissing' in this.band.dataset && this.note) {
        const { missingText = '', caseType = '', defaultModel = '' } = this.band.dataset;
        keepPlace(this.band, () => {
          this.#swap(this.original.cards, this.original.heading, this.original.label);
          this.#show(this.originalHref);
          this.note.textContent = missingText.replaceAll('[type]', caseType).replaceAll('[model]', model.name || value).replaceAll('[default]', defaultModel);
          this.note.hidden = false;
        });
      } else if (cards.length === 0) {
        keepPlace(this.band, () => {
          this.band.hidden = true;
        });
      } else {
        this.#swap(cards.map((card) => card.outerHTML).join(''), this.original.heading, this.original.label);
        this.#show(this.isDefault ? this.originalHref : href);
      }
      this.shown = key;
    } catch {}
  }

  /**
   * Visar bandet och sätter länkens adress.
   * @param {string} href
   */
  #show(href) {
    if (this.note && !this.note.hidden && href !== this.originalHref) this.note.hidden = true;
    if (this.band.hidden) {
      keepPlace(this.band, () => {
        this.band.hidden = false;
      });
    }
    if (this.link && href) this.link.setAttribute('href', href);
  }

  /**
   * @param {string} cards
   * @param {string} heading
   * @param {string} label
   */
  #swap(cards, heading, label) {
    if (!this.track) return;
    const height = this.track.getBoundingClientRect().height;
    this.track.style.minHeight = `${height}px`;
    this.track.innerHTML = cards;
    this.track.scrollLeft = 0;
    this.track.dispatchEvent(new Event('scroll'));
    if (this.heading) this.heading.textContent = heading;
    if (label) this.band.setAttribute('aria-label', label);
  }
}

document.querySelectorAll('[data-nudient-related-model]').forEach((band) => {
  if (band instanceof HTMLElement && !band.dataset.nudientRelatedModelReady) {
    band.dataset.nudientRelatedModelReady = '1';
    new NudientRelatedModel(band);
  }
});
